
import { useEffect, useRef } from 'react';

interface PerformanceTimings {
  componentName: string;
  renderTime: number;
  renderCount: number;
}

/**
 * Hook to monitor component render performance
 * @param componentName Name of the component being monitored
 * @param enabled Whether monitoring is enabled
 * @param threshold Minimum render time in ms to log (default: 5ms)
 */
export function usePerformanceMonitor(
  componentName: string,
  enabled: boolean = process.env.NODE_ENV === 'development',
  threshold: number = 5
) {
  const renderCount = useRef(0);
  const startTime = useRef(performance.now());
  
  useEffect(() => {
    if (!enabled) return;
    
    const endTime = performance.now();
    const renderTime = endTime - startTime.current;
    renderCount.current += 1;
    
    if (renderTime > threshold) {
      console.info(
        `%c[Performance] ${componentName} rendered in ${renderTime.toFixed(2)}ms (count: ${renderCount.current})`,
        'color: #8884d8'
      );
    }
    
    // Set start time for next render
    startTime.current = performance.now();
    
    // Cleanup function for unmounting
    return () => {
      if (renderCount.current > 5) {
        console.warn(
          `[Performance] ${componentName} rendered ${renderCount.current} times. Consider optimizing with React.memo or useMemo.`
        );
      }
    };
  });
  
  return {
    logRender: (message: string) => {
      if (!enabled) return;
      console.info(`[Performance] ${componentName}: ${message}`);
    }
  };
}

/**
 * Utility function to measure execution time of functions
 * @param fn Function to measure
 * @param fnName Name of the function for logging
 * @returns The same function, but with performance measurement
 */
export function withPerformanceMeasure<T extends Function>(
  fn: T, 
  fnName: string
): T {
  const wrappedFn = function(...args: any[]) {
    const start = performance.now();
    const result = fn.apply(this, args);
    const end = performance.now();
    
    if (end - start > 5) { // Only log if takes more than 5ms
      console.info(`[Performance] ${fnName} executed in ${(end - start).toFixed(2)}ms`);
    }
    
    return result;
  };
  
  return wrappedFn as unknown as T;
}
